<template lang="pug">
el-dialog.webcam-capture-dialog(
    v-focus-trap="visible"
    :visible.sync="visible"
    :close-on-click-modal="false"
    :append-to-body="true"
    :destroy-on-close="true"
    width="640px"
    top="5vh"
    @opened="startCamera"
    @closed="reset"
)
    //- The camera sits under the title: a picker when there are several.
    template(slot="title")
        .webcam-header
            span.el-dialog__title {{ $t('Take photo') }}
            el-select.webcam-camera(v-if="!capturedUrl && cameras.length > 1" v-model="cameraId" size="mini" :aria-label="$t('Camera')" @change="switchCamera")
                i.webcam-camera__icon.el-icon-fa-video.fas(slot="prefix")
                el-option(v-for="camera in cameras" :key="camera.deviceId" :value="camera.deviceId" :label="camera.label")
            span.webcam-camera-name(v-else-if="cameraLabel")
                i.el-icon-fa-video.fas
                span {{ cameraLabel }}
    //- Hovering the picture magnifies it right on the picture, like drift-zoom's
    //- inline pane: the magnified image covers the original and pans as the
    //- pointer moves. It starts at the camera's own pixels (100 %) so the
    //- focus can be judged, live and on the taken photo; the wheel changes the
    //- factor. Keyboard: focus the picture, Z toggles, arrows move, + / - zoom.
    .webcam-main(:style="{ '--ratio': stageAspectRatio }")
        .webcam-stage(
            ref="stage"
            tabindex="0"
            :class="{ 'is-zooming': zoomActive }"
            :aria-label="$t('Picture. Z toggles the magnifier, arrow keys move it, plus and minus change the factor.')"
            @pointerenter="onZoomPointer"
            @pointermove="onZoomPointer"
            @pointerleave="onZoomLeave"
            @wheel="onZoomWheel"
            @keydown="onZoomKeydown"
        )
            video.webcam-video(v-show="!capturedUrl" ref="video" autoplay playsinline muted :style="videoStyle" @loadedmetadata="onVideoMetadataLoaded" @resize="onVideoMetadataLoaded")
            img.webcam-preview(v-if="capturedUrl" :src="capturedUrl")
            canvas.webcam-zoom(ref="zoomCanvas" v-show="zoomActive")
            canvas.webcam-canvas(ref="canvas")
            .webcam-pill.webcam-magnifier(v-if="!error" role="group" @pointerenter.stop="onZoomLeave" @pointermove.stop="")
                button.webcam-pill__button(type="button" :aria-pressed="String(zoomLocked)" :title="$t('Magnify')" :aria-label="$t('Magnify')" @click="toggleZoomLock")
                    i.el-icon-fa-magnifying-glass.fas
                span.webcam-pill__value(v-if="zoomActive" aria-live="polite") {{ zoomFactorLabel }}
            //- Rotation acts on the picture, so it sits on it: the angle between
            //- the buttons is the state. Set before a camera runs, it is kept.
            .webcam-pill.webcam-rotation(v-if="rotatable && !capturedUrl" role="group" :aria-label="$t('Rotation')" @pointerenter.stop="onZoomLeave" @pointermove.stop="")
                button.webcam-pill__button(type="button" :title="$t('Rotate left')" :aria-label="$t('Rotate left')" @click="rotate(-90)")
                    i.el-icon-fa-rotate-left.fas
                span.webcam-pill__value(aria-live="polite") {{ rotation }}°
                button.webcam-pill__button(type="button" :title="$t('Rotate right')" :aria-label="$t('Rotate right')" @click="rotate(90)")
                    i.el-icon-fa-rotate-right.fas
            .webcam-error(v-if="error")
                i.el-icon-fa-video-slash.fas
                span {{ error }}
                span.webcam-error__hint(v-if="noCamera") {{ $t('To use an iPhone as the camera, place it near the Mac in landscape, lock it and leave it still with the screen off. It is then offered here automatically.') }}
                el-button(v-if="noCamera" size="mini" @click="startCamera") {{ $t('Try again') }}
                a.webcam-error__help(v-else :href="cameraHelpUrl" target="_blank" rel="noopener noreferrer") {{ cameraHelpLabel }}
    //- One centred bar on the stage's axis: the formats, each drawn as its
    //- own shape.
    .webcam-controls(v-if="!capturedUrl")
        el-radio-group.webcam-formats(v-model="aspectRatioKey" size="small" :aria-label="$t('Format')" @change="persistAspectRatio")
            el-radio-button(v-for="option in aspectRatios" :key="option.key" :label="option.key")
                span.webcam-format__frame
                    span.webcam-format__shape(:class="`is-${option.key === 'free' || option.key === 'custom' ? option.key : 'fixed'}`" :style="formatShapeStyle(option.key)")
                span {{ formatLabel(option) }}
        //- Landscape or portrait: the presets are stored once, the orientation turns them.
        el-radio-group.webcam-formats(v-if="orientable" v-model="orientation" size="small" :aria-label="$t('Orientation')" @change="persistAspectRatio")
            el-radio-button(label="landscape" :title="$t('Landscape')" :aria-label="$t('Landscape')")
                span.webcam-format__frame
                    span.webcam-format__shape.is-fixed(:style="{ width: '14px', height: '9px' }")
            el-radio-button(label="portrait" :title="$t('Portrait')" :aria-label="$t('Portrait')")
                span.webcam-format__frame
                    span.webcam-format__shape.is-fixed(:style="{ width: '9px', height: '14px' }")
        el-input.webcam-formats__custom(
            v-if="aspectRatioKey === 'custom'"
            v-model="customRatioInput"
            size="small"
            :aria-label="$t('Custom')"
            :placeholder="$t('e.g. {example}', { example: '3:2' })"
            @keyup.enter.native="applyCustomRatio"
            @blur="applyCustomRatio"
        )
    template(slot="footer")
        el-button(size="mini" icon="el-icon-fa-times fas" @click="close") {{ $t('Cancel') }}
        el-button(
            v-if="!capturedUrl"
            size="mini"
            type="primary"
            icon="el-icon-fa-camera fas"
            :disabled="!streamReady"
            data-autofocus
            @click="capture"
        ) {{ $t('Take photo') }}
        el-button(
            v-if="capturedUrl"
            size="mini"
            icon="el-icon-fa-rotate-left fas"
            @click="retake"
        ) {{ $t('Retake') }}
        el-button(
            v-if="capturedUrl"
            size="mini"
            type="success"
            icon="el-icon-fa-check fas"
            data-autofocus
            @click="accept"
        ) {{ $t('Use photo') }}
</template>

<i18n>
{
    "de": {
        "Take photo": "Foto aufnehmen",
        "Cancel": "Abbrechen",
        "Retake": "Neues Foto",
        "Use photo": "Übernehmen",
        "Format": "Format",
        "Free": "Frei",
        "Custom": "Eigenes Format",
        "Own": "Eigenes",
        "e.g. {example}": "z. B. {example}",
        "Invalid format": "Ungültiges Format",
        "Enter a ratio like {example} (width:height)": "Verhältnis wie {example} eingeben (Breite:Höhe)",
        "Camera unavailable": "Kamera nicht verfügbar",
        "Could not access your camera. Please check permissions and try again.": "Kein Zugriff auf die Kamera möglich. Bitte Berechtigungen prüfen und erneut versuchen.",
        "Your browser does not support camera access.": "Ihr Browser unterstützt keinen Kamerazugriff.",
        "No camera found.": "Keine Kamera gefunden.",
        "To use an iPhone as the camera, place it near the Mac in landscape, lock it and leave it still with the screen off. It is then offered here automatically.": "Um ein iPhone als Kamera zu nutzen: neben den Mac legen, Querformat, sperren und mit ausgeschaltetem Bildschirm ruhig liegen lassen. Es wird dann hier automatisch angeboten.",
        "Try again": "Erneut versuchen",
        "New camera found": "Neue Kamera gefunden",
        "Use {camera}?": "{camera} verwenden?",
        "Use camera": "Kamera verwenden",
        "Keep current camera": "Aktuelle Kamera behalten",
        "Search instructions for {parts}": "Anleitung für {parts} suchen",
        "Search for instructions": "Anleitung suchen",
        "Rotation": "Drehung",
        "Rotate left": "Nach links drehen",
        "Rotate right": "Nach rechts drehen",
        "Camera": "Kamera",
        "Camera {number}": "Kamera {number}",
        "Orientation": "Ausrichtung",
        "Landscape": "Querformat",
        "Portrait": "Hochformat",
        "Magnify": "Vergrößern",
        "Picture. Z toggles the magnifier, arrow keys move it, plus and minus change the factor.": "Bild. Z schaltet die Lupe um, Pfeiltasten bewegen sie, Plus und Minus ändern den Faktor."
    },
    "en": {
        "Camera {number}": "Camera {number}",
        "Own": "Custom",
        "Enter a ratio like {example} (width:height)": "Enter a ratio like {example} (width:height)",
        "Search instructions for {parts}": "Search instructions for {parts}",
        "Use {camera}?": "Use {camera}?",
        "e.g. {example}": "e.g. {example}",
        "Picture. Z toggles the magnifier, arrow keys move it, plus and minus change the factor.": "Picture. Z toggles the magnifier, arrow keys move it, plus and minus change the factor."
    }
}
</i18n>

<script>
import _ from 'lodash';
import { useAuthStore, useSetupStore } from '@/stores';

// Per-user preference (last-used framing), stored the same way as other UI-only
// settings — see CLAUDE-memory reference_user_settings_misc: a generic
// key/value entry in settings.misc, no backend schema change needed.
const ASPECT_RATIO_SETTING_KEY = 'ex:shop_product:webcam_aspect_ratio';
// A webcam mounted sideways (a bottle-photo rig) needs the frame turned before
// upload; remembered per user like the framing.
const ROTATION_SETTING_KEY = 'ex:shop_product:webcam_rotation';
// The camera the user picked (deviceId); a Continuity Camera iPhone or a second
// webcam is just another video input, the browser never asks which one to use.
const CAMERA_SETTING_KEY = 'ex:shop_product:webcam_device';
// Landscape or portrait for the fixed formats; stored apart from the format itself.
const ORIENTATION_SETTING_KEY = 'ex:shop_product:webcam_orientation';
// Formats saved before the orientation existed named the portrait shape directly.
const LEGACY_PORTRAIT_FORMATS = { '4:5': '5:4', '3:4': '4:3', '9:16': '16:9' };
// The magnifier's factor is device pixels per camera pixel: 1 shows every camera
// pixel as one screen pixel, which is what a focus check needs; the wheel moves it.
const MIN_ZOOM_FACTOR = 0.5;
const MAX_ZOOM_FACTOR = 6;
const ZOOM_WHEEL_STEP = 1.15;
const ZOOM_PAN_STEP = 0.05;
// Long-edge cap (px) for the captured output: a 4K camera's full frame, while an
// accidental 8K+ frame still stays within normal upload and image processing bounds.
const MAX_CAPTURE_DIMENSION = 4096;

export default {
    name: 'WebcamCaptureDialog',
    props: {
        visible: {
            type: Boolean,
            default: false
        },
        // Shows the 90° rotation controls; the captured image is rotated the
        // same way, so a sideways camera still yields an upright photo.
        rotatable: {
            type: Boolean,
            default: false
        }
    },
    data() {
        return {
            stream: null,
            streamReady: false,
            // Device label is only readable AFTER getUserMedia grants permission
            // (browsers withhold it beforehand for fingerprinting/privacy reasons).
            cameraLabel: '',
            capturedBlob: null,
            capturedUrl: null,
            error: null,
            noCamera: false,
            // Native aspect ratio of the camera stream — used as the box ratio while
            // "Free" is selected, so the live preview isn't cropped to an arbitrary
            // default before the stream's real dimensions are known.
            videoAspect: 4 / 3,
            aspectRatioKey: 'free',
            // 1:1 and 5:4 (4:5 turned) are the standard webshop product-image ratios; 4:3
            // and 16:9 are the general-purpose framings. Fixed formats are listed
            // landscape; the orientation switch turns them (3:4, 4:5, 9:16).
            aspectRatios: [
                { key: 'free', label: this.$t('Free') },
                { key: '1:1', label: '1:1' },
                { key: '5:4', label: '5:4' },
                { key: '4:3', label: '4:3' },
                { key: '16:9', label: '16:9' },
                { key: 'custom', label: this.$t('Own') }
            ],
            orientation: 'landscape',
            // Magnifier: pointer position on the picture (0..1), whether it hovers or
            // was switched on by keyboard, and the factor.
            zoomHover: false,
            zoomLocked: false,
            zoomPoint: { x: 0.5, y: 0.5 },
            zoomFactor: 1,
            zoomFrame: null,
            customRatioInput: '',
            customRatioValue: '',
            rotation: 0,
            cameras: [],
            cameraId: ''
        };
    },
    computed: {
        user() {
            return useAuthStore().user;
        },
        setupStore() {
            return useSetupStore();
        },
        // Camera permission/hardware issues are handled completely differently per
        // browser and OS (Chrome site settings vs. macOS System Settings vs. Windows
        // privacy settings, etc.) — rather than maintaining our own instructions for
        // every combination, point at a search tailored to what the user is actually
        // running so they land on the right one.
        cameraHelpUrl() {
            const { browser, os } = this.detectBrowserAndOs();
            const query = [browser, os, 'Kamera Zugriff erlauben Browser Einstellungen']
                .filter(Boolean)
                .join(' ');
            return `https://www.google.com/search?q=${encodeURIComponent(query)}`;
        },
        cameraHelpLabel() {
            const { browser, os } = this.detectBrowserAndOs();
            const parts = [browser, os].filter(Boolean).join(' / ');
            return parts
                ? this.$t('Search instructions for {parts}', { parts })
                : this.$t('Search for instructions');
        },
        // The camera's ratio as it appears on the stage: turned by 90° or 270°
        // a landscape stream shows portrait.
        effectiveVideoAspect() {
            return this.rotation % 180 === 0 ? this.videoAspect : 1 / this.videoAspect;
        },
        // Sized so the turned frame still covers the stage: a 90° turn swaps
        // the element's width and height, and percentages resolve against the
        // stage's width and height respectively.
        videoStyle() {
            if (this.rotation % 180 === 0) {
                return { transform: `rotate(${this.rotation}deg)` };
            }
            const ratio = this.stageAspectRatio;
            return {
                width: `${100 / ratio}%`,
                height: `${100 * ratio}%`,
                transform: `rotate(${this.rotation}deg)`
            };
        },
        orientable() {
            return ['free', 'custom', '1:1'].indexOf(this.aspectRatioKey) === -1;
        },
        zoomActive() {
            return !this.error && (this.zoomHover || this.zoomLocked);
        },
        zoomFactorLabel() {
            return `${Math.round(this.zoomFactor * 100)} %`;
        },
        stageAspectRatio() {
            if (this.aspectRatioKey === 'free') return this.effectiveVideoAspect;
            if (this.aspectRatioKey === 'custom') {
                // Live as you type — falls back to the last committed value (or the
                // camera's own ratio) while the typed text isn't a parseable ratio yet,
                // e.g. mid-way through "3:2".
                return this.parseRatio(this.customRatioInput)
                    || this.parseRatio(this.customRatioValue)
                    || this.effectiveVideoAspect;
            }
            return this.presetRatio(this.aspectRatioKey) || this.effectiveVideoAspect;
        }
    },
    created() {
        // Load-only: assigning these here must NOT round-trip through
        // persistAspectRatio() — saveMiscSettings() ends in a mutation that calls
        // apolloClient.clearStore() (see UserApi.update), and this component mounts
        // alongside the product's own in-flight fetch. Calling it from here raced
        // that fetch and killed it ("Store reset while query was in flight") on every
        // page load. Persistence below is wired to explicit user actions instead
        // (the radio group's @change, applyCustomRatio) — never to a data watcher.
        const misc = _.get(this.user, 'settings.misc') || [];
        const setting = misc.find(item => item && item.key === ASPECT_RATIO_SETTING_KEY);
        const saved = _.get(setting, 'value');

        const orientationSetting = misc.find(item => item && item.key === ORIENTATION_SETTING_KEY);
        if (_.get(orientationSetting, 'value') === 'portrait') this.orientation = 'portrait';

        if (saved && Object.keys(LEGACY_PORTRAIT_FORMATS).indexOf(saved) !== -1) {
            this.aspectRatioKey = LEGACY_PORTRAIT_FORMATS[saved];
            this.orientation = 'portrait';
        } else if (saved && saved.indexOf('custom:') === 0) {
            this.customRatioValue = saved.slice('custom:'.length);
            this.customRatioInput = this.customRatioValue;
            this.aspectRatioKey = 'custom';
        } else if (saved && this.aspectRatios.some(option => option.key === saved)) {
            this.aspectRatioKey = saved;
        }

        const cameraSetting = misc.find(item => item && item.key === CAMERA_SETTING_KEY);
        this.cameraId = _.get(cameraSetting, 'value') || '';

        if (this.rotatable) {
            const rotationSetting = misc.find(item => item && item.key === ROTATION_SETTING_KEY);
            const rotation = parseInt(_.get(rotationSetting, 'value'), 10);
            if ([0, 90, 180, 270].indexOf(rotation) !== -1) this.rotation = rotation;
        }
    },
    // Keyboard: Enter always hits the step's main action. "Take photo" is
    // disabled until the stream runs and "Use photo" appears only after the
    // shot, so the focus follows them (v-focus-trap keeps it in the dialog).
    watch: {
        zoomActive(active) {
            if (active) this.zoomFrame = requestAnimationFrame(this.drawZoom);
            else this.stopZoomLoop();
        },
        streamReady(ready) {
            if (ready) this.focusPrimaryAction();
        },
        capturedUrl() {
            this.focusPrimaryAction();
        },
    },
    methods: {
        focusPrimaryAction() {
            this.$nextTick(() => {
                const button = [...this.$el.querySelectorAll('[data-autofocus]')].find(element => !element.disabled && element.offsetParent);
                if (button) button.focus();
            });
        },
        detectBrowserAndOs() {
            const ua = navigator.userAgent || '';

            let browser = '';
            if (/Edg\//.test(ua)) browser = 'Edge';
            else if (/OPR\//.test(ua)) browser = 'Opera';
            else if (/Firefox\//.test(ua)) browser = 'Firefox';
            else if (/Chrome\//.test(ua)) browser = 'Chrome';
            else if (/Safari\//.test(ua)) browser = 'Safari';

            let os = '';
            if (/Windows/.test(ua)) os = 'Windows';
            else if (/Mac OS X/.test(ua)) os = 'macOS';
            else if (/Android/.test(ua)) os = 'Android';
            else if (/iPhone|iPad|iPod/.test(ua)) os = 'iOS';
            else if (/Linux/.test(ua)) os = 'Linux';

            return { browser, os };
        },
        rotate(delta) {
            this.rotation = (this.rotation + delta + 360) % 360;
            this.setupStore.saveMiscSettings({ key: ROTATION_SETTING_KEY, value: String(this.rotation) });
        },
        // The frame the crop works on: the live video, or, when rotated, the
        // video drawn turned onto an offscreen canvas so the crop and the
        // saved image match what the stage shows.
        captureSource() {
            const video = this.$refs.video;
            const nativeWidth = video.videoWidth;
            const nativeHeight = video.videoHeight;
            if (!nativeWidth || !nativeHeight) return null;
            if (this.rotation === 0) return { source: video, width: nativeWidth, height: nativeHeight };

            const turned = this.rotation % 180 !== 0;
            const canvas = document.createElement('canvas');
            canvas.width = turned ? nativeHeight : nativeWidth;
            canvas.height = turned ? nativeWidth : nativeHeight;
            const context = canvas.getContext('2d');
            context.translate(canvas.width / 2, canvas.height / 2);
            context.rotate((this.rotation * Math.PI) / 180);
            context.drawImage(video, -nativeWidth / 2, -nativeHeight / 2, nativeWidth, nativeHeight);

            return { source: canvas, width: canvas.width, height: canvas.height };
        },
        persistAspectRatio() {
            const value = this.aspectRatioKey === 'custom'
                ? `custom:${this.customRatioValue}`
                : this.aspectRatioKey;
            this.setupStore.saveMiscSettings({ key: ASPECT_RATIO_SETTING_KEY, value });
            this.setupStore.saveMiscSettings({ key: ORIENTATION_SETTING_KEY, value: this.orientation });
        },
        // A fixed format's ratio in the chosen orientation (listed landscape).
        presetRatio(key) {
            const ratio = this.parseRatio(key);
            if (!ratio) return null;
            return this.orientation === 'portrait' ? 1 / ratio : ratio;
        },
        formatLabel(option) {
            if (this.orientation !== 'portrait' || ['free', 'custom', '1:1'].indexOf(option.key) !== -1) return option.label;
            return option.key.split(':').reverse().join(':');
        },
        // Where the pointer is on the picture.
        onZoomPointer(event) {
            const stage = this.$refs.stage;
            if (!stage || this.error) return;
            const rect = stage.getBoundingClientRect();
            this.zoomPoint = {
                x: _.clamp((event.clientX - rect.left) / rect.width, 0, 1),
                y: _.clamp((event.clientY - rect.top) / rect.height, 0, 1)
            };
            this.zoomHover = true;
        },
        onZoomLeave() {
            this.zoomHover = false;
        },
        changeZoomFactor(multiplier) {
            this.zoomFactor = _.clamp(this.zoomFactor * multiplier, MIN_ZOOM_FACTOR, MAX_ZOOM_FACTOR);
        },
        onZoomWheel(event) {
            if (!this.zoomActive) return;
            event.preventDefault();
            this.changeZoomFactor(event.deltaY < 0 ? ZOOM_WHEEL_STEP : 1 / ZOOM_WHEEL_STEP);
        },
        toggleZoomLock() {
            this.zoomLocked = !this.zoomLocked;
        },
        stopZoomLoop() {
            if (this.zoomFrame) cancelAnimationFrame(this.zoomFrame);
            this.zoomFrame = null;
        },
        // The area of the camera frame the picture shows: the frame centre-cropped
        // to the chosen ratio, the same area the photo gets.
        cropRect(nativeWidth, nativeHeight) {
            const targetRatio = this.stageAspectRatio;
            let width = nativeWidth;
            let height = nativeHeight;
            if (nativeWidth / nativeHeight > targetRatio) {
                width = Math.round(nativeHeight * targetRatio);
            } else {
                height = Math.round(nativeWidth / targetRatio);
            }

            return { x: Math.round((nativeWidth - width) / 2), y: Math.round((nativeHeight - height) / 2), width, height };
        },
        // One frame of the magnifier, drawn over the picture: the camera's pixels,
        // each `zoomFactor` device pixels wide. As in drift-zoom the magnified area
        // follows the pointer across the whole picture, so every edge is reachable.
        drawZoom() {
            this.zoomFrame = null;
            if (!this.zoomActive) return;
            this.zoomFrame = requestAnimationFrame(this.drawZoom);

            const stage = this.$refs.stage;
            const canvas = this.$refs.zoomCanvas;
            if (!stage || !canvas) return;

            let source;
            let crop;
            if (this.capturedUrl) {
                source = this.$refs.canvas;
                crop = { x: 0, y: 0, width: source.width, height: source.height };
            } else {
                const frame = this.captureSource();
                if (!frame) return;
                source = frame.source;
                crop = this.cropRect(frame.width, frame.height);
            }
            if (!crop.width || !crop.height) return;

            const ratio = window.devicePixelRatio || 1;
            const width = Math.max(1, Math.round(stage.clientWidth * ratio));
            const height = Math.max(1, Math.round(stage.clientHeight * ratio));
            if (canvas.width !== width) canvas.width = width;
            if (canvas.height !== height) canvas.height = height;

            const factor = this.zoomFactor;
            const regionWidth = Math.min(crop.width, width / factor);
            const regionHeight = Math.min(crop.height, height / factor);
            const regionX = crop.x + this.zoomPoint.x * (crop.width - regionWidth);
            const regionY = crop.y + this.zoomPoint.y * (crop.height - regionHeight);

            const context = canvas.getContext('2d');
            context.fillStyle = '#000';
            context.fillRect(0, 0, width, height);
            // Magnified pixels stay square: judging sharpness on smoothed pixels is guesswork.
            context.imageSmoothingEnabled = factor < 1;
            const drawWidth = regionWidth * factor;
            const drawHeight = regionHeight * factor;
            context.drawImage(source, regionX, regionY, regionWidth, regionHeight, (width - drawWidth) / 2, (height - drawHeight) / 2, drawWidth, drawHeight);
        },
        onZoomKeydown(event) {
            if (event.key === 'z' || event.key === 'Z') {
                event.preventDefault();
                this.toggleZoomLock();
                return;
            }
            if (!this.zoomActive) return;
            const pan = { ArrowLeft: [-1, 0], ArrowRight: [1, 0], ArrowUp: [0, -1], ArrowDown: [0, 1] }[event.key];
            if (pan) {
                this.zoomPoint = {
                    x: _.clamp(this.zoomPoint.x + pan[0] * ZOOM_PAN_STEP, 0, 1),
                    y: _.clamp(this.zoomPoint.y + pan[1] * ZOOM_PAN_STEP, 0, 1)
                };
            } else if (event.key === '+' || event.key === '=') {
                this.changeZoomFactor(ZOOM_WHEEL_STEP);
            } else if (event.key === '-') {
                this.changeZoomFactor(1 / ZOOM_WHEEL_STEP);
            } else {
                return;
            }
            event.preventDefault();
        },
        // The format chip's little frame, drawn at the format's own proportion
        // inside a 14 x 14 box; "Frei" shows the camera's ratio, "Eigenes" the
        // typed one (both dashed, see the style).
        formatShapeStyle(key) {
            let ratio = this.presetRatio(key);
            if (key === 'free') ratio = this.effectiveVideoAspect;
            if (key === 'custom') ratio = this.parseRatio(this.customRatioInput) || this.parseRatio(this.customRatioValue) || 3 / 2;
            const size = 14;
            const width = ratio >= 1 ? size : Math.max(5, Math.round(size * ratio));
            const height = ratio >= 1 ? Math.max(5, Math.round(size / ratio)) : size;

            return { width: `${width}px`, height: `${height}px` };
        },
        // Accepts "W:H" or "W/H" (e.g. "3:2", "21:9"); a bare number is also
        // accepted as a direct ratio.
        parseRatio(input) {
            if (!input) return null;
            const match = String(input).trim().match(/^(\d+(?:\.\d+)?)\s*[:/]\s*(\d+(?:\.\d+)?)$/);
            if (match) {
                const width = parseFloat(match[1]);
                const height = parseFloat(match[2]);
                if (width > 0 && height > 0) return width / height;
                return null;
            }
            const single = parseFloat(input);
            return single > 0 ? single : null;
        },
        applyCustomRatio() {
            const ratio = this.parseRatio(this.customRatioInput);
            if (!ratio) {
                this.$notify.error({
                    title: this.$t('Invalid format'),
                    message: this.$t('Enter a ratio like {example} (width:height)', { example: '3:2' }),
                    iconClass: 'fas el-icon-fa-xmark',
                    position: 'bottom-left',
                    customClass: 'dark',
                    duration: 8000
                });
                return;
            }
            this.customRatioValue = this.customRatioInput.trim();
            if (this.aspectRatioKey === 'custom') this.persistAspectRatio();
        },
        async startCamera() {
            this.error = null;
            this.noCamera = false;
            this.streamReady = false;

            if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
                this.error = this.$t('Your browser does not support camera access.');
                return;
            }

            // Registered before the first attempt: browsers only list an iPhone
            // (Continuity Camera) once it lies locked, landscape and still, so a
            // dialog opened too early has no camera at all until that moment.
            navigator.mediaDevices.addEventListener('devicechange', this.onDeviceChange);

            try {
                // Without explicit width/height constraints browsers default to a modest
                // stream resolution (often ~640x480) rather than the camera's native one;
                // the capture crop reads video.videoWidth/videoHeight, so the stream's
                // resolution IS the photo's. The 4K ideal is the start (the browser clamps
                // it to the closest mode the camera has); useLargestResolution() then asks
                // for the camera's own maximum where the browser reports it.
                this.stream = await navigator.mediaDevices.getUserMedia({
                    video: {
                        ...(this.cameraId ? { deviceId: { exact: this.cameraId } } : { facingMode: 'environment' }),
                        width: { ideal: 3840 },
                        height: { ideal: 2160 }
                    },
                    audio: false
                });
                await this.useLargestResolution();
                this.$refs.video.srcObject = this.stream;
                this.streamReady = true;
                this.cameraLabel = _.get(this.stream.getVideoTracks(), '[0].label', '');
                await this.listCameras();
            } catch (err) {
                // The remembered camera (an iPhone that is not around today)
                // is not available: fall back to the default one once.
                if (this.cameraId) {
                    this.cameraId = '';
                    return this.startCamera();
                }
                this.noCamera = ['NotFoundError', 'DevicesNotFoundError', 'OverconstrainedError'].indexOf(err.name) !== -1;
                if (this.noCamera) {
                    this.error = this.$t('No camera found.');
                    return;
                }
                this.error = this.$t('Could not access your camera. Please check permissions and try again.');
                this.$notify.error({
                    title: this.$t('Camera unavailable'),
                    message: this.error,
                    iconClass: 'fas el-icon-fa-video-slash',
                    position: 'bottom-left',
                    customClass: 'dark',
                    duration: 8000
                });
            }
        },
        // Video inputs the browser knows, labelled once permission is granted;
        // the one the stream runs on is the current pick. Also re-run when a
        // device appears or disappears (an iPhone joining as Continuity Camera).
        async listCameras() {
            if (!navigator.mediaDevices.enumerateDevices) return;
            const devices = await navigator.mediaDevices.enumerateDevices();
            this.cameras = devices
                .filter(device => device.kind === 'videoinput')
                .map((device, index) => ({ deviceId: device.deviceId, label: device.label || this.$t('Camera {number}', { number: index + 1 }) }));
            const current = this.stream && _.get(this.stream.getVideoTracks(), '[0]');
            const currentId = current && current.getSettings ? current.getSettings().deviceId : null;
            if (currentId && this.cameras.some(camera => camera.deviceId === currentId)) this.cameraId = currentId;
        },
        // No stream yet: the first camera to appear starts it. Stream running:
        // a camera that joins meanwhile (an iPhone placed next to the Mac) is
        // offered, the way conferencing web apps do.
        async onDeviceChange() {
            if (!this.stream) return this.startCamera();
            const known = this.cameras.map(camera => camera.deviceId);
            await this.listCameras();
            const added = this.cameras.find(camera => known.indexOf(camera.deviceId) === -1);
            if (!added) return;
            try {
                await this.$confirm(this.$t('Use {camera}?', { camera: added.label }), this.$t('New camera found'), {
                    confirmButtonText: this.$t('Use camera'),
                    cancelButtonText: this.$t('Keep current camera'),
                    type: 'info'
                });
            } catch (cancelled) {
                return;
            }
            this.cameraId = added.deviceId;
            await this.switchCamera(added.deviceId);
        },
        async switchCamera(deviceId) {
            this.setupStore.saveMiscSettings({ key: CAMERA_SETTING_KEY, value: deviceId });
            this.stopStream();
            await this.startCamera();
        },
        stopStream() {
            navigator.mediaDevices && navigator.mediaDevices.removeEventListener('devicechange', this.onDeviceChange);
            if (this.stream) {
                this.stream.getTracks().forEach(track => track.stop());
                this.stream = null;
            }
            this.streamReady = false;
            this.cameraLabel = '';
        },
        // An ideal only picks the mode closest to it, and a 16:9 ideal can land on a
        // smaller 16:9 mode of a 4:3 sensor. The track's capabilities name the
        // camera's largest frame (Chromium and Safari; Firefox reports none and
        // keeps the ideal above). A camera refusing the mode keeps the running one.
        async useLargestResolution() {
            const track = _.get(this.stream.getVideoTracks(), '[0]');
            if (!track || typeof track.getCapabilities !== 'function') return;
            const capabilities = track.getCapabilities();
            const width = _.get(capabilities, 'width.max');
            const height = _.get(capabilities, 'height.max');
            if (!width || !height) return;
            try {
                await track.applyConstraints({ width: { ideal: width }, height: { ideal: height } });
            } catch (error) {
                // the stream keeps the mode it started with
            }
        },
        // Also on `resize`: the stream's mode can change after it started.
        onVideoMetadataLoaded() {
            const video = this.$refs.video;
            if (video && video.videoWidth && video.videoHeight) {
                this.videoAspect = video.videoWidth / video.videoHeight;
            }
        },
        // Grabs the current video frame onto the (hidden) canvas and turns it into a
        // JPEG Blob for preview + later upload — the video keeps streaming underneath
        // so "Retake" can capture again without re-requesting the camera. The crop
        // matches whatever ratio the preview box is showing (center-cropped) so what
        // you see is what you get, instead of always uploading the full native frame.
        capture() {
            const canvas = this.$refs.canvas;
            const frame = this.captureSource();
            if (!frame) return;
            const { source, width: nativeWidth, height: nativeHeight } = frame;

            const { x: offsetX, y: offsetY, width: cropWidth, height: cropHeight } = this.cropRect(nativeWidth, nativeHeight);

            // Second safety net on top of the getUserMedia resolution cap: bounds the
            // long edge of the OUTPUT (post-crop) image, so a device that ignores the
            // requested ideal and hands back something larger still can't produce an
            // oversized upload.
            let outputWidth = cropWidth;
            let outputHeight = cropHeight;
            if (Math.max(cropWidth, cropHeight) > MAX_CAPTURE_DIMENSION) {
                const scale = MAX_CAPTURE_DIMENSION / Math.max(cropWidth, cropHeight);
                outputWidth = Math.round(cropWidth * scale);
                outputHeight = Math.round(cropHeight * scale);
            }

            canvas.width = outputWidth;
            canvas.height = outputHeight;
            canvas.getContext('2d').drawImage(
                source,
                offsetX, offsetY, cropWidth, cropHeight,
                0, 0, outputWidth, outputHeight
            );

            canvas.toBlob((blob) => {
                if (!blob) return;
                if (this.capturedUrl) URL.revokeObjectURL(this.capturedUrl);
                this.capturedBlob = blob;
                this.capturedUrl = URL.createObjectURL(blob);
            }, 'image/jpeg', 0.92);
        },
        retake() {
            if (this.capturedUrl) URL.revokeObjectURL(this.capturedUrl);
            this.capturedUrl = null;
            this.capturedBlob = null;
        },
        accept() {
            if (!this.capturedBlob) return;
            this.$emit('captured', this.capturedBlob);
            this.close();
        },
        close() {
            this.$emit('update:visible', false);
        },
        // Deliberately does NOT reset aspectRatioKey/customRatioValue — the chosen
        // framing is a remembered preference (settings.misc), not per-session state.
        reset() {
            this.stopStream();
            this.zoomHover = false;
            this.zoomLocked = false;
            this.stopZoomLoop();
            if (this.capturedUrl) URL.revokeObjectURL(this.capturedUrl);
            this.capturedUrl = null;
            this.capturedBlob = null;
            this.error = null;
        }
    },
    beforeDestroy() {
        this.stopZoomLoop();
        this.stopStream();
    }
};
</script>

<style lang="sass" scoped>
// The dialog itself must never exceed the viewport: the header/toolbar/footer are
// fixed-height chrome, so the video stage is what has to give — capped to a share of
// the viewport height (portrait ratios like 9:16 would otherwise blow way past it),
// and the body scrolls as a fallback on very short windows instead of overflowing.
::v-deep .el-dialog
    max-height: 90vh
    display: flex
    flex-direction: column

::v-deep .el-dialog__body
    overflow-y: auto
    flex: 1 1 auto

// Sized from HEIGHT, not width: with width:100% + aspect-ratio + max-height, a
// browser clamps the computed height but does NOT reliably shrink the (already
// definite) width to match — fine for landscape ratios, but a 9:16 box then keeps
// its full dialog-width and just gets visually squashed/overflows. Making height
// the definite axis (capped) and width auto lets the browser derive width FROM
// height via aspect-ratio, which DOES stay ratio-correct at any max-width clamp too.
.webcam-main
    --stage-height: min(56vh, 480px)
    display: flex
    justify-content: center

.webcam-stage
    position: relative
    // Width is the capped one and the height follows from the ratio: a fixed
    // height with only a max-width left wide formats squashed in the dialog.
    width: min(100%, calc(var(--stage-height) * var(--ratio)))
    aspect-ratio: var(--ratio)
    background: #000
    border-radius: 4px
    overflow: hidden
    display: flex
    align-items: center
    justify-content: center
    outline-offset: 2px
    &.is-zooming
        cursor: crosshair

// The magnifier over the picture; the pointer passes through to the picture.
.webcam-zoom
    position: absolute
    inset: 0
    width: 100%
    height: 100%
    pointer-events: none

.webcam-video,
.webcam-preview
    width: 100%
    height: 100%
    object-fit: cover

.webcam-canvas
    display: none

.webcam-header
    display: flex
    flex-direction: column
    align-items: flex-start
    gap: 6px
    padding-right: 24px

.webcam-camera
    width: 260px
    max-width: 100%
    .webcam-camera__icon
        line-height: 28px
        padding-left: 4px
        color: #909399

.webcam-camera-name
    display: inline-flex
    align-items: center
    gap: 6px
    max-width: 100%
    font-size: 12px
    color: #909399
    span
        white-space: nowrap
        overflow: hidden
        text-overflow: ellipsis

// Centred under the stage, which is centred too; wraps as a whole on a
// narrow window.
.webcam-controls
    display: flex
    flex-wrap: wrap
    align-items: center
    justify-content: center
    gap: 10px
    margin-top: 16px

// On the stage: dark glass over the picture, rotation bottom right,
// magnifier bottom left.
.webcam-pill
    position: absolute
    bottom: 12px
    z-index: 1
    display: flex
    align-items: center
    height: 32px
    padding: 0 2px
    border-radius: 16px
    background: rgba(0, 0, 0, 0.55)
    color: #fff

.webcam-rotation
    right: 12px

.webcam-magnifier
    left: 12px
    // The factor ends the pill, so it needs its own space on the right.
    .webcam-pill__value
        min-width: 0
        padding: 0 12px 0 2px

.webcam-pill__button
    display: flex
    align-items: center
    justify-content: center
    width: 30px
    height: 30px
    margin: 0
    padding: 0
    border: 0
    border-radius: 50%
    background: transparent
    box-shadow: none
    color: inherit
    font-size: 13px
    cursor: pointer
    &:hover
        background: rgba(255, 255, 255, 0.16)
    &:focus-visible
        outline: 2px solid #fff
        outline-offset: -2px
    &[aria-pressed="true"]
        background: rgba(255, 255, 255, 0.28)

.webcam-pill__value
    min-width: 2.6em
    text-align: center
    font-size: 12px
    font-variant-numeric: tabular-nums

// The admin's segmented control, each segment led by the format's shape.
.webcam-formats
    ::v-deep .el-radio-button__inner
        display: inline-flex
        align-items: center
        gap: 6px
        height: 32px
        padding: 0 10px
        font-size: 12px

.webcam-format__frame
    display: flex
    flex: none
    align-items: center
    justify-content: center
    width: 14px
    height: 14px

.webcam-format__shape
    display: block
    flex: none
    box-sizing: border-box
    border: 1.5px solid currentColor
    border-radius: 1px
    &.is-free,
    &.is-custom
        border-style: dashed

.webcam-formats__custom
    width: 90px

.webcam-error
    position: absolute
    inset: 0
    display: flex
    flex-direction: column
    align-items: center
    justify-content: center
    gap: 10px
    color: #fff
    font-size: 13px
    padding: 0 20px
    text-align: center
    background: rgba(0, 0, 0, 0.6)
    i
        font-size: 28px

.webcam-error__hint
    font-size: 12px
    color: #dcdfe6
    max-width: 420px

.webcam-error__help
    color: #a0cfff
    text-decoration: underline
    font-size: 12px
    &:hover
        color: #d9ecff
</style>
